import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  generateImage,
  fetchMediaByCustomer,
  type AspectRatio,
  type GeneratedImage,
} from "../../../../../services/contentService";
import {
  getMediaThumbnailUrl,
  isImageMediaItem,
  type MediaItem,
} from "../../../../../types/MediaItem";

type PickerTab = "ai" | "library";

interface BlogImagePickerProps {
  isOpen: boolean;
  customerId: string;
  idToken: string;
  /** Heading shown in the modal (e.g. "Featured image" or "Insert image"). */
  title?: string;
  /** Pre-fills the AI prompt the first time the modal opens. */
  defaultPrompt?: string;
  /** Aspect ratio used for AI generation. Hero = 16x9, inline default = 16x9. */
  aspectRatio?: AspectRatio;
  /** Which tab to open on. "library" lands straight on the media grid. */
  initialTab?: PickerTab;
  /** `info.mediaId` is the media-library id (AI images are saved there too);
   *  `info.alt` a starting description (the AI prompt's subject). */
  onSelect: (url: string, info?: { mediaId?: string; alt?: string }) => void;
  onClose: () => void;
}

const overlayClass =
  "fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4";
const cardClass =
  "flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl";
const tabBtn = (active: boolean) =>
  `flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
    active ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
  }`;

/**
 * Modal that lets the user source a blog image two ways:
 *  - "Generate with AI" — calls the existing /generate-image pipeline.
 *  - "From library"     — picks an existing image from the customer media library.
 * Returns the chosen image's URL via onSelect. Used for both the featured/hero
 * image and for inserting inline images into the blog body.
 */
export const BlogImagePicker: React.FC<BlogImagePickerProps> = ({
  isOpen,
  customerId,
  idToken,
  title = "Add image",
  defaultPrompt = "",
  aspectRatio = "16x9",
  initialTab = "ai",
  onSelect,
  onClose,
}) => {
  const [tab, setTab] = useState<PickerTab>(initialTab);

  // AI generation state
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<GeneratedImage[]>([]);

  // Library state
  const [library, setLibrary] = useState<MediaItem[]>([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState<string | null>(null);
  const [libLoaded, setLibLoaded] = useState(false);

  // Reset transient state each time the modal is (re)opened.
  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    setPrompt(defaultPrompt);
    setGenerating(false);
    setGenError(null);
    setGenerated([]);
    setLibError(null);
    setLibLoaded(false);
  }, [isOpen, defaultPrompt, initialTab]);

  const loadLibrary = useCallback(async () => {
    if (!customerId || !idToken) return;
    setLibLoading(true);
    setLibError(null);
    try {
      const { medias } = await fetchMediaByCustomer(customerId, idToken, {
        mediaType: "image",
        limit: 60,
      });
      setLibrary(medias.filter(isImageMediaItem));
    } catch (e: unknown) {
      setLibError(e instanceof Error ? e.message : "Failed to load media library");
    } finally {
      setLibLoading(false);
      setLibLoaded(true);
    }
  }, [customerId, idToken]);

  // Lazy-load the library only when its tab is first opened.
  useEffect(() => {
    if (isOpen && tab === "library" && !libLoaded && !libLoading) {
      loadLibrary();
    }
  }, [isOpen, tab, libLoaded, libLoading, loadLibrary]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setGenError("Describe the image you want to generate.");
      return;
    }
    setGenerating(true);
    setGenError(null);
    setGenerated([]);
    try {
      const res = await generateImage(
        {
          prompt: prompt.trim(),
          customer_id: customerId,
          provider: "gemini",
          num_images: 2,
          aspect_ratio: aspectRatio,
        },
        idToken
      );
      if (!res.success) {
        setGenError(res.error || "Failed to generate images");
        return;
      }
      setGenerated(res.images.filter((img) => Boolean(img.image_url)));
      if (res.images.length === 0) {
        setGenError("No images were generated. Try a different prompt.");
      }
    } catch (e: unknown) {
      setGenError(e instanceof Error ? e.message : "Failed to generate images");
    } finally {
      setGenerating(false);
    }
  };

  const choose = (url: string, info?: { mediaId?: string; alt?: string }) => {
    if (!url) return;
    onSelect(url, info);
    onClose();
  };

  // The prompt's first sentence describes the picture well enough to start from.
  const altFromPrompt = (p?: string) =>
    (p || "").split(/(?<=\.)\s/)[0].replace(/^Editorial (hero |illustrative )?image for a blog post (titled|about)\s*/i, "Image for ").slice(0, 200);

  const libraryImages = useMemo(
    () =>
      library
        .map((m) => ({ id: m.media_id, url: getMediaThumbnailUrl(m) }))
        .filter((m) => Boolean(m.url)),
    [library]
  );

  if (!isOpen) return null;

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={cardClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close image picker"
          >
            <span className="material-icons text-[20px]">close</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4">
          <div className="inline-flex w-full rounded-lg bg-gray-100 p-1">
            <button type="button" className={tabBtn(tab === "ai")} onClick={() => setTab("ai")}>
              Generate with AI
            </button>
            <button
              type="button"
              className={tabBtn(tab === "library")}
              onClick={() => setTab("library")}
            >
              From library
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === "ai" ? (
            <div className="space-y-3">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the image you want, e.g. a bright modern office desk with a laptop, clean editorial style, no text"
                rows={6}
                className="block max-h-72 min-h-[8rem] w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm leading-relaxed text-gray-800 outline-none focus:border-theme-primary focus:ring-1 focus:ring-theme-primary"
              />
              {genError && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {genError}
                </p>
              )}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Generating…
                  </>
                ) : (
                  "Generate"
                )}
              </button>

              {generated.length > 0 && (
                <>
                  <p className="pt-1 text-xs font-medium text-gray-500">
                    Pick one to use:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {generated.map((img) => (
                      <button
                        key={img.image_index}
                        type="button"
                        onClick={() => choose(img.image_url, { mediaId: img.media_id, alt: altFromPrompt(img.metadata?.prompt || prompt) })}
                        className="group relative overflow-hidden rounded-lg border border-gray-200 hover:border-theme-primary focus:outline-none focus:ring-2 focus:ring-theme-primary"
                      >
                        <img
                          src={img.image_url}
                          alt="AI generated option"
                          className="aspect-video w-full object-cover"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-sm font-semibold text-white opacity-0 transition-opacity group-hover:bg-black/40 group-hover:opacity-100">
                          Use this
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              {libLoading ? (
                <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                  Loading your media…
                </div>
              ) : libError ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {libError}
                </p>
              ) : libraryImages.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  No images in your library yet. Generate one with AI instead.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {libraryImages.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => choose(m.url, { mediaId: m.id })}
                      className="group relative overflow-hidden rounded-lg border border-gray-200 hover:border-theme-primary focus:outline-none focus:ring-2 focus:ring-theme-primary"
                    >
                      <img
                        src={m.url}
                        alt="Library option"
                        className="aspect-square w-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogImagePicker;
